import { api } from "../api/Middleware";
import {
  CUSTOMER_PAYMENTS_CREATE_SUBSCRIPTION_WITH_PROFILE_URL,
  CUSTOMER_BILLING_PROFILES_LIST_URL,
} from "../api/Urls";

// =============================
// CUSTOMER PAYMENTS SERVICES
// =============================

// Listar perfis de faturamento para seleção (com filtros específicos para pagamentos)
export const listBillingProfilesForPayment = async (params = {}) => {
  // Filtros padrões para pagamentos
  const defaultParams = {
    billing_type: "CREDIT_CARD", // Apenas cartão de crédito para assinaturas
    is_active: true,
    ...params
  };
  
  return await api.get(CUSTOMER_BILLING_PROFILES_LIST_URL, { params: defaultParams });
};

// Criar assinatura com perfil de faturamento existente
export const createSubscriptionWithProfile = async (data) => {
  return await api.post(CUSTOMER_PAYMENTS_CREATE_SUBSCRIPTION_WITH_PROFILE_URL, data);
};

// =============================
// HELPER FUNCTIONS
// =============================

// Validar dados da assinatura antes de enviar
export const validateSubscriptionData = (data) => {
  const errors = {};

  if (!data.billing_profile_id) {
    errors.billing_profile_id = "Perfil de faturamento é obrigatório";
  }

  if (!data.value || parseFloat(data.value) <= 0) {
    errors.value = "Valor deve ser maior que zero";
  }

  if (!data.description || data.description.trim().length < 5) {
    errors.description = "Descrição é obrigatória e deve ter pelo menos 5 caracteres";
  }

  if (!data.next_due_date) {
    errors.next_due_date = "Data do próximo vencimento é obrigatória";
  }

  if (!data.cycle) {
    errors.cycle = "Ciclo de cobrança é obrigatório";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Formatar valor para exibição
export const formatCurrency = (value) => {
  if (!value) return "R$ 0,00";
  
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(parseFloat(value));
};

// Formatar data para exibição
export const formatDate = (dateString) => {
  if (!dateString) return "—";
  
  try {
    return new Date(dateString).toLocaleDateString("pt-BR");
  } catch {
    return "Data inválida";
  }
};

// Obter data atual para vencimento
export const getCurrentDate = () => {
  let now = new Date();
  now = now.toISOString().split("T")[0];
  now = now.split("-").reverse().join("/");

  return now // Format: DD/MM/YYYY
};

// Obter opções de ciclo de cobrança
export const getBillingCycles = () => {
  return [
    { value: "MONTHLY", label: "Mensal" },
    { value: "QUARTERLY", label: "Trimestral" },
    { value: "SEMIANNUAL", label: "Semestral" },
    { value: "ANNUAL", label: "Anual" },
  ];
};

// Formatar perfil de faturamento para select
export const formatBillingProfileForSelect = (profile) => {
  const cardInfo = profile.card_last_digits 
    ? `${profile.card_brand || 'Cartão'} **** ${profile.card_last_digits}`
    : profile.profile_name;
    
  const isDefault = profile.is_default ? ' (Padrão)' : '';
  
  return {
    value: profile.id,
    label: `${profile.profile_name} - ${cardInfo}${isDefault}`,
    profile: profile
  };
};

// Processar resposta da API de criação de assinatura
export const processSubscriptionResponse = (response) => {
  const data = response.data;
  
  return {
    success: data.success,
    subscriptionId: data.subscription_id,
    status: data.status,
    value: data.value,
    nextDueDate: data.next_due_date,
    cycle: data.cycle,
    message: data.message,
    billingProfile: data.billing_profile_used,
    apiResponse: data.api_response,
    
    // Campos formatados
    formattedValue: formatCurrency(data.value),
    formattedNextDueDate: formatDate(data.next_due_date),
    statusVariant: getStatusVariant(data.status),
    statusText: getStatusText(data.status),
  };
};

// Obter variante do Bootstrap para status da assinatura
export const getStatusVariant = (status) => {
  const statusMap = {
    ACTIVE: "success",
    PENDING: "warning",
    SUSPENDED: "danger",
    CANCELED: "secondary",
  };
  return statusMap[status] || "secondary";
};

// Obter texto legível para status da assinatura
export const getStatusText = (status) => {
  const statusMap = {
    ACTIVE: "Ativa",
    PENDING: "Pendente",
    SUSPENDED: "Suspensa",
    CANCELED: "Cancelada",
  };
  return statusMap[status] || "Desconhecido";
};

// Processar erros da API
export const processSubscriptionError = (error) => {
  const errorData = error.response?.data;
  let errorMessage = "Não foi possível processar o pagamento.";
  
  if (errorData) {
    if (typeof errorData === "string") {
      errorMessage = errorData;
    } else if (errorData.detail) {
      errorMessage = errorData.detail;
    } else if (errorData.message) {
      errorMessage = errorData.message;
    } else if (errorData.error) {
      errorMessage = errorData.error;
    } else if (errorData.billing_profile_id) {
      errorMessage = `Perfil de faturamento: ${Array.isArray(errorData.billing_profile_id) ? errorData.billing_profile_id[0] : errorData.billing_profile_id}`;
    } else if (errorData.value) {
      errorMessage = `Valor: ${Array.isArray(errorData.value) ? errorData.value[0] : errorData.value}`;
    }
  }
  
  return {
    message: errorMessage,
    status: error.response?.status,
    data: errorData,
  };
};

export default {
  listBillingProfilesForPayment,
  createSubscriptionWithProfile,
  validateSubscriptionData,
  formatCurrency,
  formatDate,
  getCurrentDate,
  getBillingCycles,
  formatBillingProfileForSelect,
  processSubscriptionResponse,
  getStatusVariant,
  getStatusText,
  processSubscriptionError,
};